import React, { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";

// @mui/styles components
import { makeStyles } from "@mui/styles";
import makeAnimated from "react-select/animated";

// @mui/icons-material
import Close from "@mui/icons-material/Close";
import ExtensionIcon from "@mui/icons-material/Extension";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountBoxIcon from "@mui/icons-material/AccountBox";

// core components
import InputAdornment from "@mui/material/InputAdornment";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";
import Snackbars from "components/Snackbar/Snackbar.js";
import CustomTabs from "components/AlternativeTabs/AlternativeTabs";
import Select from "react-select";

// Hooks
import { useColorTheme } from "hooks/useColorTheme";

// validation
import { verifyMaxLength } from "helpers/validation";

// styles
import {
  TitleWrapperStyled,
  TitleStyled,
  styles,
} from "./newGiotankStyles";

// action creators
import { sendNewGiotank, clearError, setError } from "redux/ducks/giotanks";
import { selectsDictionary } from "./newGiotankDictionary";

const useStyles = makeStyles(styles);
const animatedComponents = makeAnimated();

function NewGiotank({ onClose = null }) {
  const dispatch = useDispatch();
  // selectors
  const accounts = useSelector((state) => state.giotanks.accounts);
  const isSaving = useSelector((state) => state.giotanks.isLoading);
  const colorBg = useSelector((state) => state.config.colorBg);
  const error = useSelector((state) => state.giotanks.error, shallowEqual);
  const errorMessage = useSelector(
    (state) => state.giotanks.errorMessage,
    shallowEqual
  );
  // Custom hooks
  const { color } = useColorTheme(colorBg);
  const classes = useStyles();
  const history = useHistory();
  // new form
  const [tab, setTab] = React.useState(0);
  const [selects, setSelects] = React.useState({
    accounts: null,
    units: null,
  });
  const [serialNumber, setSerialNumber] = React.useState("");

  // validation state
  const [serialNumberState, setSerialNumberState] = React.useState("");

  // calls
  const clearSnackbar = useCallback(() => {
    dispatch(clearError());
  }, []);
  const setSnackbar = useCallback(
    (error) => {
      dispatch(setError(error));
    },
    []
  );

  // handlers
  const handleTabs = useCallback((value) => {
    setTab(value);
  }, []);
  const handleChangeCombo = useCallback((event, value) => {
    setSelects((prevState) => {
      return { ...prevState, [event]: value };
    });
  }, []);
  const handleSubmit = () => {
    if (tab) {
      if (!selects.units) {
        setSnackbar("El campo de unidad es obligatorio.");
        setSerialNumberState("error");
        return;
      }
    } else {
      if (!selects.accounts) {
        setSnackbar("El campo de cuenta es obligatorio.");
        setSerialNumberState("error");
        return;
      }
    }

    if (serialNumberState === "" || serialNumberState === "error") {
      setSnackbar("El campo de numero de serie es obligatorio.");
      setSerialNumberState("error");
      return;
    }

    const body = {
      [tab ? "idUnit" : "idAccount"]: tab
        ? selects.units.value
        : selects.accounts.value,
      serialNumber,
    };

    dispatch(sendNewGiotank(body, onClose));
  };
  const handleChangeSerialNumber = (event) => {
    if (verifyMaxLength(event.target.value, 14) && event.target.value) {
      setSerialNumberState("success");
    } else {
      setSerialNumberState("error");
    }
    setSerialNumber(event.target.value);
  };

  const handleGoBack = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    history.goBack();
  }, [history, onClose]);

  return (
    <React.Fragment>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color={color} icon>
              <CardIcon color={color}>
                <ExtensionIcon />
              </CardIcon>
              <TitleWrapperStyled>
                <TitleStyled>
                  Asignación de giotank
                </TitleStyled>
                <Button
                  justIcon
                  round
                  color={color}
                  onClick={() => handleGoBack()}
                >
                  <ArrowBackIcon className={classes.buttonIcon} />
                </Button>
              </TitleWrapperStyled>
            </CardHeader>
            <CardBody>
              <CustomTabs
                headerColor="info"
                plainTabs
                onChange={handleTabs}
                tabs={[
                  {
                    tabName: "Cuentas",
                    tabIcon: AccountBoxIcon,
                    tabContent: (
                      <Select
                        placeholder="Cuentas..."
                        name="accounts"
                        components={animatedComponents}
                        options={accounts}
                        value={selects.accounts}
                        onChange={(val) =>
                          handleChangeCombo(selectsDictionary.accounts, val)
                        }
                        className="list-select__container "
                        classNamePrefix="select"
                      />
                    ),
                  },
                ]}
              />
              <form>
                <CustomInput
                  success={serialNumberState === "success"}
                  error={serialNumberState === "error"}
                  labelText="Numero de serie *"
                  id="serialNumber"
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    onChange: (event) => handleChangeSerialNumber(event),
                    type: "Text",
                    value: serialNumber,
                    endAdornment:
                      serialNumberState === "error" ? (
                        <InputAdornment position="end">
                          <Close className={classes.danger} />
                        </InputAdornment>
                      ) : undefined,
                  }}
                />
                <div className={classes.formCategory}>
                  <small>*</small> Campos requeridos
                </div>
                <Button
                  color="info"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className={classes.registerButton}
                >
                  Guardar
                </Button>
              </form>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
      <Snackbars
        place="tr"
        color="danger"
        message={errorMessage}
        open={error}
        closeNotification={() => clearSnackbar()}
        close
      />
    </React.Fragment>
  );
}

export default React.memo(NewGiotank);
